import React, { useEffect, useState } from "react";
import styles from "./css/PublicCourses.module.css";
import PublicCourseCard from "../Components/PublicCourseCard";
import EnrolledCourseCard from "../Components/EnrolledCourseCard";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getAllCourses, clearCourses } from "../../../redux/actions/courses";
import { CircularProgress, Grid } from "@mui/material";
import { thumbnailList } from "../../../redux/constants/commonFunctions";
import CreateNewCourseCard from "../Components/CreateNewCourseCard";
import AICourseBanner from "../Components/AICourseBanner";
import AICourseGeneratorModal from "../Components/AICourseGeneratorModal";

const PublicCourses = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isAIModalOpen, setIsAIModalOpen] = useState(false);

  const publicCourses = useSelector((state) => state?.courses?.publicCourses) || [];
  const visibleCourses = useSelector((state) => state?.courses?.visibleCourses) || [];

  const [combinedCourses, setCombinedCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const userDataString = localStorage.getItem("userData");
  const userData = userDataString ? JSON.parse(userDataString) : null;
  const ownerId = Number(userData?.profile_id);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    const fetchAll = async () => {
      try {
        await Promise.all([
          dispatch(getAllCourses(null, true)),   // public = true
          dispatch(getAllCourses(null, false)),  // public = false
        ]);
      } catch (err) {
        console.error("course fetch error:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      mounted = false;
      dispatch(clearCourses());
    };
  }, [dispatch]);

  useEffect(() => {
    const map = new Map();

    publicCourses.forEach((c) => {
      const id = c?.id ?? c?.node?.id;
      if (id) map.set(id, c);
    });

    visibleCourses.forEach((c) => {
      const id = c?.id ?? c?.node?.id;
      if (id) map.set(id, c);
    });

    const merged = Array.from(map.values());

    setCombinedCourses(merged);
  }, [publicCourses, visibleCourses]);


  const enrolledCourses = combinedCourses.filter(
    (course) => course?.is_enrolled && course?.owner?.id !== ownerId
  );

  const availableCourses = combinedCourses.filter((course) => {
    const isCourseOwner = course?.owner?.id === ownerId;
    const isEnrolled = course?.is_enrolled;

    if (isCourseOwner) {
      return true;
    } else if (isEnrolled) {
      return false;
    } else {
      return course?.is_draft === false;
    }
  });

  // Separate owner's courses from other available courses
  const ownerCourses = availableCourses.filter((course) => course?.owner?.id === ownerId);
  const otherAvailableCourses = availableCourses.filter((course) => course?.owner?.id !== ownerId);
  const totalVisibleCoursesCount =
    ownerCourses.length + otherAvailableCourses.length + enrolledCourses.length;

  return (
    <>
      {isLoading ? (
        <div className={styles.loader_style}>
          <CircularProgress sx={{ color: "#F33070" }} size={50} />
        </div>
      ) : (
        <div className={styles.CoursesContainer}>
          <AICourseBanner onClick={() => setIsAIModalOpen(true)} />

          <AICourseGeneratorModal
            open={isAIModalOpen}
            onClose={() => setIsAIModalOpen(false)}
          />

          {/* Created Courses Section */}
          {ownerCourses.length > 0 && (
            <>
              <div className={styles.CreatedCourseHeadingContainer}>
                <div className={styles.CreatedCourseHeadingWithBar}>
                  <div className={styles.greenBar}></div>
                  <div className={styles.CreatedCourseHeading}>Created Courses</div>
                </div>
              </div>

              <div className={styles.CreatedCoursesHorizontalScroll}>
                <div className={styles.createCourseCardWrapper}>
                  <CreateNewCourseCard />
                </div>

                {ownerCourses.map((course, index) => (
                  <div key={course?.id} className={styles.createdCourseCardWrapper}>
                    <PublicCourseCard
                      id={course?.id}
                      image={course?.thumbnail ?? thumbnailList[index % 13]}
                      title={course?.name}
                      instructor={course?.owner.name}
                      description={course?.description}
                      course_level={course?.course_level}
                      studentCount={course?.members_count}
                      price={course?.price}
                      isEnrolled={course?.is_enrolled}
                      isActive={course?.active}
                      lectureCount={course?.total_lecs}
                      courseLength={course?.course_mins}
                      discounted_price={course?.discounted_price}
                      isOwner={course?.owner?.id === ownerId}
                      isDrafted={course?.is_draft}
                      isLive={course?.isLive}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Enrolled Courses Section */}
          {enrolledCourses.length > 0 && (
            <div className={styles.EnrolledCoursesSection}>
              <div className={styles.EnrolledCourseHeadingContainer}>
                <div className={styles.EnrolledCourseHeadingWithBar}>
                  <div className={styles.greenBar}></div>
                  <div className={styles.EnrolledCourseHeading}>Enrolled Courses</div>
                </div>
              </div>

              <div className={styles.EnrolledCourseCardsHorizontalScroll}>
                {enrolledCourses.map((course, index) => (
                  <div key={course?.id} className={styles.enrolledCourseCardWrapper}>
                    <EnrolledCourseCard
                      id={course?.id}
                      image={course?.thumbnail ?? thumbnailList[index % 13]}
                      title={course?.name}
                      instructor={course?.owner.name}
                      description={course?.description}
                      studentCount={course?.members_count}
                      price={course?.price}
                      isActive={course?.active}
                      lectureCount={course?.total_lecs}
                      courseLength={course?.course_mins}
                      discounted_price={course?.discounted_price}
                      progress={course?.progress_bar}
                      isLive={course?.isLive}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Public Courses Section */}
          {!(totalVisibleCoursesCount > 1 && otherAvailableCourses.length === 0) && (
            <div className={styles.CourseHeadingContainer}>
              {/* <img src="/Image/CourseMonetisation/publicCourse.svg" alt="" />
              <div className={styles.CourseHeading}>{t("common.courses")}</div> */}
              <div className={styles.sectionDivider}>
                <span>Available Courses</span>
              </div>
            </div>
          )}

          <div className={styles.CourseCardsVerticalScroll}>
            {ownerCourses.length === 0 && <CreateNewCourseCard />}

            {totalVisibleCoursesCount > 0 ? (
              otherAvailableCourses.length > 0 ? (
                // Only show public courses here (you had a .filter(course => course?.public) previously)
                otherAvailableCourses
                  .filter((course) => course?.public)
                  .map((course, index) => (
                    <PublicCourseCard
                      key={course?.id}
                      id={course?.id}
                      image={course?.thumbnail ?? thumbnailList[index % 13]}
                      title={course?.name}
                      instructor={course?.owner.name}
                      description={course?.description}
                      course_level={course?.course_level}
                      studentCount={course?.members_count}
                      price={course?.price}
                      isEnrolled={course?.is_enrolled}
                      isActive={course?.active}
                      lectureCount={course?.total_lecs}
                      courseLength={course?.course_mins}
                      discounted_price={course?.discounted_price}
                      isOwner={course?.owner?.id === ownerId}
                      isDrafted={course?.is_draft}
                      isLive={course?.isLive}
                    />
                  ))
              ) : null
            ) : (
              <Grid
                sx={{
                  width: "100%",
                  height: "100%",
                  margin: "0 auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "2rem",
                  position: {
                    xs: "static",
                    md: "absolute",
                  },
                  top: {
                    md: "5%",
                  },
                  pointerEvents: "none",
                }}
              >
                <img
                  src="Image/CourseMonetisation/noCourseIllustration.svg"
                  alt="no course found"
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    height: "auto",
                  }}
                />
              </Grid>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PublicCourses;